/**
 * The Schematic Plan drawing the reviewer made in this browser tab, kept in
 * sessionStorage under `schematic_<application number>` until the
 * certificate step reads it.
 *
 * The drawing screen stores two kinds of value under that key: while the
 * canvas is open it stores the whole picture as a data: URL (several hundred
 * KB), and its Next button replaces that with the web address of the
 * uploaded picture. Only the web address may go to the certificate: the
 * certificate-data request is JSON, and the backend refuses JSON bodies over
 * 100 KB, so a leftover picture failed the certificate with "Something went
 * wrong!". That happened when a reviewer opened Draw schematic first and then
 * chose Upload file.
 */

const keyFor = (applicationNumber: string | number | undefined) =>
  `schematic_${applicationNumber}`;

/** True for a picture stored inline (data: URL) instead of a web address. */
const isInlinePicture = (value: string) => value.trim().startsWith("data:");

/**
 * The saved drawing's web address, or null when nothing usable is saved.
 * A leftover inline picture is never returned.
 */
export function readSavedSchematicDrawing(
  applicationNumber: string | number | undefined
): string | null {
  const value = sessionStorage.getItem(keyFor(applicationNumber));
  if (!value || isInlinePicture(value)) return null;
  return value;
}

/** Forgets the drawing saved in this tab, for example once a file is chosen instead. */
export function clearSavedSchematicDrawing(applicationNumber: string | number | undefined): void {
  sessionStorage.removeItem(keyFor(applicationNumber));
}
